import { RecurrenceRule } from "@clockwise/client-commons/src/datatypes/RecurrenceRule";
import {
  ConferencingType,
  EventCategory,
  FlexDetailsInput,
  FlexRange,
  ProposalState,
  ResponseStatusEnum,
} from "@clockwise/schema/v2";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { DateTime, Interval } from "luxon";
import React, { ReactNode, createContext, useCallback, useContext, useReducer } from "react";
import invariant from "tiny-invariant";
import { TradeoffBlock } from "../reschedule-confirmation-modal/RescheduleConfirmationModal";
import { PersistedProposalProvider } from "./PersistedProposalContext";

export type Person = {
  email: string;
  displayName: string;
  givenName: string | null;
  familyName: string | null;
  externalImageUrl: string | null;
  isMe: boolean;
  id: string;
};

export type Attendee = {
  id: string;
  isOptional: boolean;
  person: Person;
  user: {
    id: string;
  } | null;
};

interface EventForProposal {
  externalEventId: string;
  title: string;
  description?: string | null;
  dateOrTimeRange?: string | null;
  isAllDay: boolean;
  attendees: Array<{
    person: {
      email: string;
    };
  }>;
  recurrenceRule?: string | null;
  conferencing?: {
    type: ConferencingType;
  } | null;
  location?: string | null;
  flexibility?: FlexDetailsInput | null;
  category: EventCategory;
  responseStatus: ResponseStatusEnum;
}

export type CurrentProposal = {
  proposalId: string | null;
  title: string;
  startTime: DateTime;
  endTime: DateTime;
  timeZone: string;
  allDay: boolean;
  initialAttendeeIds: string[];
  attendees: Attendee[];
  description: string;
  recurrenceRule: RecurrenceRule | null;
  conferenceType: ConferencingType | null;
  location: string | null;
  initialized: boolean;
  flexDetails: FlexDetailsInput;
  meta: {
    isDragging: boolean;
  };
  eventId?: string;
  responseStatus?: ResponseStatusEnum;
  eventCategory?: EventCategory;
};

type CurrentProposalReadObj = {
  currentProposal: CurrentProposal;
};

type CurrentProposalWriteObj = {
  initializeFromEvent: <E extends EventForProposal>(event: E) => void;
  initializeProposal: (proposal: Omit<CurrentProposal, "initialized">) => void;
  initOrRescheduleProposal: (proposal: Omit<CurrentProposal, "initialized">) => void;
  updateTitle: (title: string) => void;
  updateTime: (startTime: DateTime, endTime: DateTime) => void;
  updateAllDay: (allDay: boolean) => void;
  updateDescription: (description: string) => void;
  setAttendeeCalendarIds: (attendeeCalendarIds: string[]) => void;
  setAttendees: (attendees: Attendee[]) => void;
  addAttendee: (attendee: Attendee) => void;
  removeAttendee: (email: string) => void;
  updateAttendeeOptionality: (email: string, isOptional: boolean) => void;
  updateRecurrenceRule: (recurrenceRule: RecurrenceRule) => void;
  updateConferenceType: (conferenceType: ConferencingType) => void;
  updateLocation: (location: string) => void;
  clearProposal: () => void;
  updateFlexDetails: (flexDetails: FlexDetailsInput) => void;
  updateProposalMeta: (meta: Partial<CurrentProposal["meta"]>) => void;
  updateResponseStatus: (responseStatus: ResponseStatusEnum) => void;
  updateEventCategory: (eventCategory: EventCategory) => void;
};

type InitProposalFromEventAction<E extends EventForProposal> = {
  type: "InitProposalFromEvent";
  event: E;
};

type InitProposalAction = {
  type: "InitProposal";
  proposal: Omit<CurrentProposal, "initialized">;
};

type InitOrRescheduleProposalAction = {
  type: "InitOrRescheduleProposal";
  proposal: Omit<CurrentProposal, "initialized">;
};

type ClearProposalAction = {
  type: "ClearProposal";
};

type UpdateTitleAction = {
  type: "UpdateTitle";
  title: string;
};

type UpdateTimeAction = {
  type: "UpdateTime";
  startTime: DateTime;
  endTime: DateTime;
};

type UpdateAllDayAction = {
  type: "UpdateAllDay";
  allDay: boolean;
};

type UpdateDescriptionAction = {
  description: string;
  type: "UpdateDescription";
};

type SetAttendeeCalendarIdsAction = {
  type: "SetAttendeeCalendarIds";
  initialAttendeeIds: string[];
};

type SetAttendeesAction = {
  type: "SetAttendees";
  attendees: Attendee[];
};

type AddAttendeeAction = {
  type: "AddAttendee";
  attendee: Attendee;
};

type RemoveAttendeeAction = {
  type: "RemoveAttendee";
  email: string;
};

type UpdateAttendeeOptionalityAction = {
  type: "UpdateAttendeeOptionality";
  email: string;
  isOptional: boolean;
};

type UpdateRecurrenceRuleAction = {
  type: "UpdateRecurrenceRule";
  recurrenceRule: RecurrenceRule;
};

type UpdateConferenceTypeAction = {
  type: "UpdateConferenceType";
  conferenceType: ConferencingType | null;
};

type UpdateLocationAction = {
  type: "UpdateLocation";
  location: string;
};

type UpdateFlexDetailsAction = {
  type: "UpdateFlexDetails";
  flexDetails: FlexDetailsInput;
};

type UpdatePersistedProposalAction = {
  type: "UpdatePersistedProposal";
  proposalId: string;
  proposalStatus: ProposalState;
  tradeoffBlocks?: TradeoffBlock[];
};

type UpdateProposalMetaAction = {
  type: "UpdateProposalMeta";
  meta: Partial<CurrentProposal["meta"]>;
};

type UpdateResponseStatusAction = {
  type: "UpdateResponseStatus";
  responseStatus: ResponseStatusEnum;
};

type UpdateEventCategoryAction = {
  type: "UpdateEventCategory";
  eventCategory: EventCategory;
};

type ProposalAction<E extends EventForProposal> =
  | InitProposalFromEventAction<E>
  | InitProposalAction
  | InitOrRescheduleProposalAction
  | ClearProposalAction
  | UpdateTitleAction
  | UpdateTimeAction
  | UpdateAllDayAction
  | UpdateDescriptionAction
  | SetAttendeeCalendarIdsAction
  | SetAttendeesAction
  | AddAttendeeAction
  | RemoveAttendeeAction
  | UpdateAttendeeOptionalityAction
  | UpdateRecurrenceRuleAction
  | UpdateConferenceTypeAction
  | UpdateLocationAction
  | UpdateFlexDetailsAction
  | UpdatePersistedProposalAction
  | UpdateProposalMetaAction
  | UpdateResponseStatusAction
  | UpdateEventCategoryAction;

function proposalReducer<E extends EventForProposal>(
  state: CurrentProposal,
  action: ProposalAction<E>,
) {
  switch (action.type) {
    case "InitProposalFromEvent": {
      const {
        title,
        description,
        dateOrTimeRange,
        isAllDay,
        attendees,
        recurrenceRule,
        conferencing,
        location,
        flexibility,
        externalEventId,
        category,
        responseStatus,
      } = action.event;

      if (!dateOrTimeRange) {
        return state;
      }

      const timeInterval = Interval.fromISO(dateOrTimeRange);

      return {
        proposalId: null,
        title,
        startTime: timeInterval?.start,
        endTime: timeInterval?.end,
        allDay: isAllDay,
        description: description ?? "",
        initialAttendeeIds: attendees.map((attendee) => attendee.person.email),
        conferenceType: conferencing?.type ?? null,
        location: location ?? null,
        flexDetails: flexibility ?? { isFlexible: true, flexRange: FlexRange.Day },
        attendees: [],
        recurrenceRule: recurrenceRule ? new RecurrenceRule(recurrenceRule) : null,
        eventId: externalEventId,
        eventCategory: category,
        responseStatus: responseStatus,
        timeZone: getRenderTimeZone(),
        meta: {
          isDragging: false,
        },
        initialized: true,
      };
    }
    case "InitProposal":
      return { ...action.proposal, initialized: true };
    case "InitOrRescheduleProposal": {
      if (state.initialized) {
        const duration = state.endTime.diff(state.startTime);
        return {
          ...state,
          startTime: action.proposal.startTime,
          endTime: action.proposal.startTime.plus(duration),
        };
      } else {
        return { ...action.proposal, initialized: true };
      }
    }
    case "ClearProposal":
      return { ...INITIAL_PROPOSAL };
    case "UpdateTitle":
      return {
        ...state,
        title: action.title,
      };
    case "UpdateTime":
      return {
        ...state,
        startTime: action.startTime,
        endTime: action.endTime,
      };
    case "UpdateAllDay": {
      if (action.allDay) {
        return {
          ...state,
          allDay: true,
          startTime: state.startTime.startOf("day"),
          endTime: state.startTime.plus({ days: 1 }).startOf("day"),
        };
      } else {
        // Since we set the start time to the start of the day when toggling on all day,
        // we set it back to the current time here to make it easier for the user to "see"
        // the proposal in the calendar when toggling off all day.
        const startTime = state.startTime.set({ hour: DateTime.local().hour });
        return {
          ...state,
          allDay: false,
          startTime,
          endTime: startTime.plus({ minutes: 30 }),
        };
      }
    }
    case "UpdateDescription":
      return {
        ...state,
        description: action.description,
      };
    case "SetAttendeeCalendarIds":
      return {
        ...state,
        initialAttendeeIds: action.initialAttendeeIds, // Changed from attendeeCalendarIds
      };
    case "SetAttendees":
      return {
        ...state,
        attendees: action.attendees,
      };
    case "AddAttendee": {
      return {
        ...state,
        attendees: [...state.attendees, action.attendee],
      };
    }
    case "RemoveAttendee": {
      return {
        ...state,
        attendees: state.attendees.filter((attendee) => attendee.person.email !== action.email),
      };
    }
    case "UpdateAttendeeOptionality": {
      return {
        ...state,
        attendees: state.attendees.map((attendee) =>
          attendee.person.email === action.email
            ? { ...attendee, isOptional: action.isOptional }
            : attendee,
        ),
      };
    }
    case "UpdateRecurrenceRule":
      return {
        ...state,
        recurrenceRule: action.recurrenceRule,
      };
    case "UpdateConferenceType":
      return {
        ...state,
        conferenceType: action.conferenceType,
      };

    case "UpdateLocation":
      return {
        ...state,
        location: action.location,
      };

    case "UpdateFlexDetails":
      return {
        ...state,
        flexDetails: action.flexDetails,
      };
    case "UpdatePersistedProposal":
      return {
        ...state,
        proposalId: action.proposalId,
        proposalStatus: action.proposalStatus,
        tradeoffBlocks: action.tradeoffBlocks,
      };
    case "UpdateProposalMeta":
      return {
        ...state,
        meta: {
          ...state.meta,
          ...action.meta,
        },
      };
    case "UpdateResponseStatus":
      return {
        ...state,
        responseStatus: action.responseStatus,
      };
    case "UpdateEventCategory":
      return {
        ...state,
        eventCategory: action.eventCategory,
      };
  }
}

const INITIAL_PROPOSAL: CurrentProposal = {
  proposalId: null,
  title: "",
  startTime: DateTime.local(),
  endTime: DateTime.local().plus({ hours: 1 }),
  timeZone: "America/Los_Angeles",
  allDay: false,
  description: "",
  initialAttendeeIds: [],
  attendees: [],
  recurrenceRule: null,
  conferenceType: null,
  location: null,
  initialized: false,
  flexDetails: {
    isFlexible: true,
    flexRange: FlexRange.Day,
  },
  meta: {
    isDragging: false, // Set if we are manipulating the proposal with clickDrag
  },
  eventId: undefined,
  responseStatus: undefined,
  eventCategory: undefined,
};

const ReadContext = createContext<CurrentProposalReadObj>({
  currentProposal: INITIAL_PROPOSAL,
});
const WriteContext = createContext<CurrentProposalWriteObj | null>(null);

export const CurrentProposalProvider = ({ children }: { children: ReactNode }) => {
  const [proposal, dispatch] = useReducer(proposalReducer, INITIAL_PROPOSAL);

  const handleInitializeFromEvent = <E extends EventForProposal>(event: E) => {
    dispatch({ type: "InitProposalFromEvent", event });
  };

  const handleInitializeProposal = (proposal: Omit<CurrentProposal, "initialized">) => {
    dispatch({ type: "InitProposal", proposal });
  };

  const handleInitOrRescheduleProposal = (proposal: Omit<CurrentProposal, "initialized">) => {
    dispatch({ type: "InitOrRescheduleProposal", proposal });
  };

  const handleUpdateTitle = useCallback(
    (title: string) => {
      dispatch({ type: "UpdateTitle", title });
    },
    [dispatch],
  );

  const handleUpdateTime = (startTime: DateTime, endTime: DateTime) => {
    dispatch({ type: "UpdateTime", startTime, endTime });
  };

  const handleUpdateAllDay = (allDay: boolean) => {
    dispatch({ type: "UpdateAllDay", allDay });
  };

  const handleClearProposal = () => {
    dispatch({ type: "ClearProposal" });
  };

  const handleUpdateDescription = useCallback(
    (description: string) => {
      dispatch({ type: "UpdateDescription", description });
    },
    [dispatch],
  );

  const handleSetAttendeeCalendarIds = (initialAttendeeIds: string[]) => {
    dispatch({ type: "SetAttendeeCalendarIds", initialAttendeeIds });
  };

  const handleSetAttendees = (attendees: Attendee[]) => {
    dispatch({ type: "SetAttendees", attendees });
  };

  const handleAddAttendee = (attendee: Attendee) => {
    dispatch({ type: "AddAttendee", attendee });
  };

  const handleRemoveAttendee = (email: string) => {
    dispatch({ type: "RemoveAttendee", email });
  };

  const handleUpdateAttendeeOptionality = (email: string, isOptional: boolean) => {
    dispatch({ type: "UpdateAttendeeOptionality", email, isOptional });
  };

  const handleUpdateRecurrenceRule = (recurrenceRule: RecurrenceRule) => {
    dispatch({ type: "UpdateRecurrenceRule", recurrenceRule });
  };

  const handleUpdateConferenceType = (conferenceType: ConferencingType | null) => {
    dispatch({ type: "UpdateConferenceType", conferenceType });
  };

  const handleUpdateLocation = useCallback(
    (location: string) => {
      dispatch({ type: "UpdateLocation", location });
    },
    [dispatch],
  );

  const handleUpdateFlexDetails = (flexDetails: FlexDetailsInput) => {
    dispatch({ type: "UpdateFlexDetails", flexDetails });
  };

  const handleUpdateProposalMeta = (meta: Partial<CurrentProposal["meta"]>) => {
    dispatch({ type: "UpdateProposalMeta", meta });
  };

  const handleUpdateResponseStatus = (responseStatus: ResponseStatusEnum) => {
    dispatch({ type: "UpdateResponseStatus", responseStatus });
  };

  const handleUpdateEventCategory = (eventCategory: EventCategory) => {
    dispatch({ type: "UpdateEventCategory", eventCategory });
  };

  return (
    <WriteContext.Provider
      value={{
        initializeFromEvent: handleInitializeFromEvent,
        initializeProposal: handleInitializeProposal,
        initOrRescheduleProposal: handleInitOrRescheduleProposal,
        updateTitle: handleUpdateTitle,
        clearProposal: handleClearProposal,
        updateTime: handleUpdateTime,
        updateAllDay: handleUpdateAllDay,
        updateDescription: handleUpdateDescription,
        setAttendeeCalendarIds: handleSetAttendeeCalendarIds,
        setAttendees: handleSetAttendees,
        addAttendee: handleAddAttendee,
        removeAttendee: handleRemoveAttendee,
        updateAttendeeOptionality: handleUpdateAttendeeOptionality,
        updateRecurrenceRule: handleUpdateRecurrenceRule,
        updateConferenceType: handleUpdateConferenceType,
        updateLocation: handleUpdateLocation,
        updateFlexDetails: handleUpdateFlexDetails,
        updateProposalMeta: handleUpdateProposalMeta,
        updateResponseStatus: handleUpdateResponseStatus,
        updateEventCategory: handleUpdateEventCategory,
      }}
    >
      <ReadContext.Provider value={{ currentProposal: proposal }}>
        <PersistedProposalProvider {...proposal}>{children}</PersistedProposalProvider>
      </ReadContext.Provider>
    </WriteContext.Provider>
  );
};

export const useCurrentProposal = () => useContext(ReadContext);
export const useUpdateCurrentProposal = () => {
  const context = useContext(WriteContext);
  invariant(context, "useUpdateCurrentProposal must be within CurrentProposalProvider");
  return context;
};
